/**
 * EQuIP Config Query Hooks
 *
 * React Query hooks for program configuration data.
 * Wraps equipApi.config.* with typed key factories.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { equipApi } from '@/services/equipApi'
import type {
  ProgramConfigItem,
  ProgramConfigUpdate,
  ConfigAuditResponse,
  EligibilityCriteriaRule,
  EligibilityCriterionUpdate,
  EligibilityCriteriaAuditResponse,
  MeasureScheduleItem,
  MeasureScheduleCreate,
  MeasureScheduleUpdate,
  PimsDistribution,
} from '@/lib/equip-types'
import type { SchedulerService } from '@/types/scheduler'

// ---------------------------------------------------------------------------
// Key factory
// ---------------------------------------------------------------------------

export const equipConfigKeys = {
  all: () => ['equip', 'config'] as const,
  program: () => ['equip', 'config', 'program'] as const,
  audit: (providerType: string) => ['equip', 'config', 'audit', providerType] as const,
  eligibilityCriteria: () => ['equip', 'config', 'eligibility-criteria'] as const,
  eligibilityCriteriaAudit: (ruleId: number) => ['equip', 'config', 'eligibility-criteria', 'audit', ruleId] as const,
  measureSchedule: (year: number, quarter: number) => ['equip', 'config', 'measure-schedule', year, quarter] as const,
  measureNames: () => ['equip', 'config', 'measure-names'] as const,
  pimsDistribution: () => ['equip', 'config', 'pims-distribution'] as const,
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

/**
 * Fetches program configuration (award caps, part-time units) from the API.
 * Config rarely changes — uses 5-minute staleTime to avoid unnecessary refetches.
 */
export function useProgramConfig() {
  return useQuery<ProgramConfigItem[]>({
    queryKey: equipConfigKeys.program(),
    queryFn: () => equipApi.config.getProgramConfig(),
    staleTime: 5 * 60 * 1000, // 5 minutes — config rarely changes
  })
}

/**
 * Mutation hook for updating program config financial fields.
 * Invalidates both the config list and audit cache for the affected provider type.
 */
export function useUpdateProgramConfig() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ providerType, update }: { providerType: string; update: ProgramConfigUpdate }) =>
      equipApi.config.updateProgramConfig(providerType, update),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: equipConfigKeys.program() })
      queryClient.invalidateQueries({ queryKey: equipConfigKeys.audit(variables.providerType) })
    },
  })
}

/**
 * Fetches paginated audit history for a specific provider type.
 * Enabled only when providerType is non-null.
 * Uses 2-minute staleTime since audit data may change after saves.
 */
export function useConfigAudit(providerType: string | null, params?: { offset?: number; limit?: number }) {
  return useQuery<ConfigAuditResponse>({
    queryKey: [...equipConfigKeys.audit(providerType ?? ''), params?.offset ?? 0, params?.limit ?? 5],
    queryFn: () => equipApi.config.getConfigAudit(providerType!, params),
    enabled: !!providerType,
    staleTime: 2 * 60 * 1000, // 2 minutes — audit data may change after saves
  })
}

// ---------------------------------------------------------------------------
// Eligibility criteria
// ---------------------------------------------------------------------------

/**
 * Fetches all eligibility criteria configuration rules.
 * Uses 5-minute staleTime since rules rarely change.
 */
export function useEligibilityCriteria() {
  return useQuery<EligibilityCriteriaRule[]>({
    queryKey: equipConfigKeys.eligibilityCriteria(),
    queryFn: () => equipApi.config.getEligibilityCriteria(),
    staleTime: 5 * 60 * 1000,
  })
}

/**
 * Mutation hook for updating a single eligibility criterion.
 * Invalidates the criteria list and audit cache for the affected rule.
 */
export function useUpdateEligibilityCriteriaRule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ ruleId, update }: { ruleId: number; update: EligibilityCriterionUpdate }) =>
      equipApi.config.updateEligibilityCriteriaRule(ruleId, update),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: equipConfigKeys.eligibilityCriteria() })
      queryClient.invalidateQueries({
        queryKey: equipConfigKeys.eligibilityCriteriaAudit(variables.ruleId),
      })
    },
  })
}

/**
 * Fetches paginated audit history for a specific eligibility criterion.
 * Enabled only when ruleId is non-null.
 */
export function useEligibilityCriteriaAudit(
  ruleId: number | null,
  params?: { offset?: number; limit?: number }
) {
  return useQuery<EligibilityCriteriaAuditResponse>({
    queryKey: [
      ...equipConfigKeys.eligibilityCriteriaAudit(ruleId ?? 0),
      params?.offset ?? 0,
      params?.limit ?? 5,
    ],
    queryFn: () => equipApi.config.getEligibilityCriteriaAudit(ruleId!, params),
    enabled: ruleId != null,
    staleTime: 2 * 60 * 1000,
  })
}

// ---------------------------------------------------------------------------
// Measure schedule
// ---------------------------------------------------------------------------

/**
 * Fetches the measure schedule for a given year and quarter.
 * Disabled when year/quarter are 0 (not yet loaded).
 * Uses 10-minute staleTime since schedules only change at program updates.
 */
export function useMeasureSchedule(year: number, quarter: number) {
  return useQuery<MeasureScheduleItem[]>({
    queryKey: equipConfigKeys.measureSchedule(year, quarter),
    queryFn: () => equipApi.config.getMeasureSchedule(year, quarter),
    enabled: year > 0 && quarter > 0,
    staleTime: 10 * 60 * 1000,
  })
}

export function useMeasureNames() {
  return useQuery<string[]>({
    queryKey: equipConfigKeys.measureNames(),
    queryFn: () => equipApi.config.getMeasureNames(),
    staleTime: 30 * 60 * 1000,
  })
}

export function useCreateMeasureSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: MeasureScheduleCreate) => equipApi.config.createMeasureSchedule(body),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: equipConfigKeys.measureSchedule(variables.year, variables.quarter) })
    },
  })
}

export function useUpdateMeasureSchedule(year: number, quarter: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, update }: { id: number; update: MeasureScheduleUpdate }) =>
      equipApi.config.updateMeasureSchedule(id, update),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipConfigKeys.measureSchedule(year, quarter) })
    },
  })
}

export function useDeleteMeasureSchedule(year: number, quarter: number) {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: number) => equipApi.config.deleteMeasureSchedule(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: equipConfigKeys.measureSchedule(year, quarter) })
    },
  })
}

// ---------------------------------------------------------------------------
// Scheduler timing
// ---------------------------------------------------------------------------

const API_BASE_URL = import.meta.env.VITE_API_URL || ''

/**
 * Fetches the next scheduled equip cache refresh time from the scheduler API.
 * Looks for services with "equip" in the service name (e.g., "equip_cache_performance").
 * Returns the earliest next_run_time across all equip-related scheduler services.
 */
export function useNextCacheRefresh() {
  return useQuery<string | null>({
    queryKey: ['scheduler', 'equip-next-refresh'],
    queryFn: async () => {
      const res = await fetch(`${API_BASE_URL}/api/scheduler/services`, { credentials: 'include' })
      if (!res.ok) return null
      const json = await res.json() as { success: boolean; data: { services: SchedulerService[] } }
      if (!json.success) return null
      const equipServices = json.data.services.filter(
        (s: SchedulerService) => s.service.toLowerCase().includes('equip') && s.next_run_time
      )
      if (equipServices.length === 0) return null
      // Return the earliest next_run_time
      const sorted = equipServices.sort(
        (a: SchedulerService, b: SchedulerService) =>
          new Date(a.next_run_time!).getTime() - new Date(b.next_run_time!).getTime()
      )
      return sorted[0].next_run_time
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: false, // Graceful degradation — if scheduler is down, show nothing
  })
}

export function usePimsDistribution() {
  return useQuery<PimsDistribution>({
    queryKey: equipConfigKeys.pimsDistribution(),
    queryFn: () => equipApi.config.getPimsDistribution(),
    staleTime: 10 * 60 * 1000, // scores only change on cache refresh
  })
}
